// Save this as: src/EnhancedPersonalNotesModal.jsx

import React, { useState, useEffect } from 'react';
import {
  FileText,
  X,
  RefreshCw,
  User,
  AlertCircle,
  Target,
  MessageSquare,
  Trash2,
  Edit2,
  Search,
  Calendar,
  Star,
  ChevronDown,
  ChevronUp
} from 'lucide-react';

const EnhancedPersonalNotesModal = ({ 
  showNotesPanel, 
  setShowNotesPanel, 
  currentUser 
}) => {
  // Form states
  const [noteTitle, setNoteTitle] = useState('');
  const [noteContent, setNoteContent] = useState('');
  const [noteCategory, setNoteCategory] = useState('general');
  const [noteIsImportant, setNoteIsImportant] = useState(false);
  const [noteSubmitting, setNoteSubmitting] = useState(false);

  // View states
  const [viewMode, setViewMode] = useState('add'); // 'add' or 'view'
  const [savedNotes, setSavedNotes] = useState([]);
  const [notesLoading, setNotesLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [expandedNote, setExpandedNote] = useState(null);
  const [editingNote, setEditingNote] = useState(null);

  // Fetch notes when switching to view mode
  useEffect(() => {
    if (viewMode === 'view' && showNotesPanel) {
      fetchNotes();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewMode, showNotesPanel, currentUser?.email]);

  const API_BASE = 'https://test.vdpl.co/Candidates/Candidates';

  const fetchNotes = async () => {
    if (!currentUser?.email) return;
    setNotesLoading(true);
    try {
      const response = await fetch(`${API_BASE}/personal_notes_api.php?user_email=${encodeURIComponent(currentUser.email)}`);
      const result = await response.json();
      
      if (result.success) {
        setSavedNotes(result.notes || []);
      } else {
        console.error('Failed to fetch notes');
      }
    } catch (error) {
      console.error('Error fetching notes:', error);
    } finally {
      setNotesLoading(false);
    }
  };

  const handleAddNote = async () => {
    if (!noteTitle.trim() || !noteContent.trim()) {
      alert('Please fill in both title and content');
      return;
    }

    setNoteSubmitting(true);
    
    try {
      const response = await fetch(`${API_BASE}/personal_notes_api.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_name: `${currentUser?.first_name} ${currentUser?.last_name}`,
          user_email: currentUser?.email,
          title: noteTitle,
          content: noteContent,
          category: noteCategory,
          is_important: noteIsImportant
        })
      });

      const result = await response.json();

      if (result.success) {
        alert('Note added successfully!');
        setNoteTitle('');
        setNoteContent('');
        setNoteCategory('general');
        setNoteIsImportant(false);

        // Fetch first, then switch — so data is ready when view renders
        await fetchNotes();
        setViewMode('view');
      } else {
        alert('Failed to add note. Please try again.');
      }
    } catch (error) {
      console.error('Error adding note:', error);
      alert('Error adding note. Please try again.');
    } finally {
      setNoteSubmitting(false);
    }
  };

  const handleDeleteNote = async (noteId) => {
    // eslint-disable-next-line no-restricted-globals
    if (!confirm('Are you sure you want to delete this note?')) return;

    try {
      const response = await fetch(`${API_BASE}/personal_notes_api.php`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ id: noteId })
      });

      const result = await response.json();

      if (result.success) {
        alert('Note deleted successfully!');
        fetchNotes();
      } else {
        alert('Failed to delete note.');
      }
    } catch (error) {
      console.error('Error deleting note:', error);
      alert('Error deleting note.');
    }
  };

  const handleUpdateNote = async (noteId) => {
    if (!editingNote) return;

    try {
      const response = await fetch(`${API_BASE}/personal_notes_api.php`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: noteId,
          title: editingNote.title,
          content: editingNote.content,
          category: editingNote.category,
          is_important: editingNote.is_important
        })
      });

      const result = await response.json();

      if (result.success) {
        alert('Note updated successfully!');
        setEditingNote(null);
        fetchNotes();
      } else {
        alert('Failed to update note.');
      }
    } catch (error) {
      console.error('Error updating note:', error);
      alert('Error updating note.');
    }
  };

  const getCategoryColor = (category) => {
    const colors = {
      general: 'bg-blue-100 text-blue-800 border-blue-200',
      important: 'bg-red-100 text-red-800 border-red-200',
      reminder: 'bg-amber-100 text-amber-800 border-amber-200',
      todo: 'bg-purple-100 text-purple-800 border-purple-200',
      meeting: 'bg-green-100 text-green-800 border-green-200',
      idea: 'bg-pink-100 text-pink-800 border-pink-200'
    };
    return colors[category] || colors.general;
  };

  const filteredNotes = savedNotes.filter(note => {
    const matchesSearch = note.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         note.content.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = categoryFilter === 'all' || note.category === categoryFilter;
    return matchesSearch && matchesCategory;
  });

  useEffect(() => {
    if (showNotesPanel && savedNotes.length === 0) {
      fetchNotes();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showNotesPanel]);

  if (!showNotesPanel) return null;

  return (
    <>
      <div 
        className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 animate-fadeIn"
        onClick={() => {
          setShowNotesPanel(false);
          setViewMode('add');
          setEditingNote(null);
        }}
      />
      
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none">
        <div 
          className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden pointer-events-auto animate-slideUp border-2 border-green-200"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-green-500 to-emerald-600 p-6 rounded-t-2xl relative overflow-hidden">
            <div className="absolute inset-0 bg-white/10 backdrop-blur-sm"></div>
            <div className="relative flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-sm">
                  <FileText className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h4 className="text-xl font-bold text-white">Personal Notes</h4>
                  <p className="text-xs text-white/90">
                    {viewMode === 'add' ? 'Create a new note' : `${filteredNotes.length} notes found`}
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                {/* View Toggle */}
                <div className="flex bg-white/20 rounded-lg p-1 backdrop-blur-sm">
                  <button
                    onClick={() => setViewMode('add')}
                    className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
                      viewMode === 'add' 
                        ? 'bg-white text-green-600 shadow-md' 
                        : 'text-white hover:bg-white/10'
                    }`}
                  >
                    Add Note
                  </button>
                  <button
                    onClick={() => setViewMode('view')}
                    className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
                      viewMode === 'view' 
                        ? 'bg-white text-green-600 shadow-md' 
                        : 'text-white hover:bg-white/10'
                    }`}
                  >
                    View Notes ({savedNotes.length})
                  </button>
                </div>
                
                <button
                  onClick={() => {
                    setShowNotesPanel(false);
                    setViewMode('add');
                    setEditingNote(null);
                  }}
                  className="p-2 hover:bg-white/20 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-white" />
                </button>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="p-6 overflow-y-auto max-h-[calc(90vh-180px)]">
            {viewMode === 'add' ? (
              // Add Note Form
              <div className="space-y-4">
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-slate-700 flex items-center">
                    <FileText className="w-4 h-4 mr-2 text-green-600" />
                    Note Title
                  </label>
                  <input
                    type="text"
                    value={noteTitle}
                    onChange={(e) => setNoteTitle(e.target.value)}
                    placeholder="Enter note title..."
                    className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 text-sm bg-slate-50 hover:bg-white transition-colors"
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-semibold text-slate-700 flex items-center">
                    <Target className="w-4 h-4 mr-2 text-green-600" />
                    Category
                  </label>
                  <select
                    value={noteCategory}
                    onChange={(e) => setNoteCategory(e.target.value)}
                    className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 text-sm bg-slate-50 hover:bg-white transition-colors"
                  >
                    <option value="general">General</option>
                    <option value="important">Important</option>
                    <option value="reminder">Reminder</option>
                    <option value="todo">To-Do</option>
                    <option value="meeting">Meeting Notes</option>
                    <option value="idea">Ideas</option>
                  </select>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-semibold text-slate-700 flex items-center">
                    <MessageSquare className="w-4 h-4 mr-2 text-green-600" />
                    Note Content
                  </label>
                  <textarea
                    value={noteContent}
                    onChange={(e) => setNoteContent(e.target.value)}
                    placeholder="Write your note here..."
                    rows="8"
                    className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 text-sm resize-none bg-slate-50 hover:bg-white transition-colors"
                  />
                </div>

                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="noteImportant"
                    checked={noteIsImportant}
                    onChange={(e) => setNoteIsImportant(e.target.checked)}
                    className="w-4 h-4 text-red-600 border-gray-300 rounded focus:ring-red-500"
                  />
                  <label htmlFor="noteImportant" className="text-sm font-medium text-slate-700 flex items-center cursor-pointer">
                    <AlertCircle className="w-4 h-4 mr-1.5 text-red-600" />
                    Mark as important
                  </label>
                </div>

                <div className="bg-green-50 border border-green-200 rounded-xl p-3">
                  <p className="text-xs text-slate-600 flex items-center">
                    <User className="w-3.5 h-3.5 mr-1.5 text-green-600" />
                    Note will be saved under: <span className="font-semibold ml-1 text-slate-900">{currentUser?.first_name} {currentUser?.last_name}</span>
                  </p>
                </div>
              </div>
            ) : (
              // View Notes
              <div className="space-y-4">
                {/* Search and Filter */}
                <div className="flex flex-col sm:flex-row gap-3">
                  <div className="flex-1 relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
                    <input
                      type="text"
                      placeholder="Search notes..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 border-2 border-slate-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 text-sm"
                    />
                  </div>
                  <select
                    value={categoryFilter}
                    onChange={(e) => setCategoryFilter(e.target.value)}
                    className="px-4 py-2 border-2 border-slate-200 rounded-xl focus:ring-2 focus:ring-green-500 text-sm"
                  >
                    <option value="all">All Categories</option>
                    <option value="general">General</option>
                    <option value="important">Important</option>
                    <option value="reminder">Reminder</option>
                    <option value="todo">To-Do</option>
                    <option value="meeting">Meeting Notes</option>
                    <option value="idea">Ideas</option>
                  </select>
                  <button
                    onClick={fetchNotes}
                    className="px-4 py-2 bg-green-50 text-green-600 rounded-xl hover:bg-green-100 transition-colors flex items-center gap-2"
                    disabled={notesLoading}
                  >
                    <RefreshCw className={`w-4 h-4 ${notesLoading ? 'animate-spin' : ''}`} />
                    Refresh
                  </button>
                </div>

                {/* Notes List */}
                {notesLoading ? (
                  <div className="text-center py-12">
                    <RefreshCw className="w-8 h-8 animate-spin text-green-500 mx-auto mb-4" />
                    <p className="text-slate-600">Loading notes...</p>
                  </div>
                ) : filteredNotes.length === 0 ? (
                  <div className="text-center py-12">
                    <FileText className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                    <p className="text-slate-600 font-medium mb-2">No notes found</p>
                    <p className="text-sm text-slate-500">
                      {savedNotes.length === 0 
                        ? 'Create your first note to get started' 
                        : 'Try adjusting your search or filters'}
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {filteredNotes.map((note) => (
                      <div 
                        key={note.id}
                        className={`border-2 rounded-xl p-4 transition-all hover:shadow-md ${
                          note.is_important ? 'border-red-300 bg-red-50' : 'border-slate-200 bg-white'
                        }`}
                      >
                        {editingNote?.id === note.id ? (
                          // Edit Mode
                          <div className="space-y-3">
                            <input
                              type="text"
                              value={editingNote.title}
                              onChange={(e) => setEditingNote({...editingNote, title: e.target.value})}
                              className="w-full px-3 py-2 border-2 border-slate-200 rounded-lg text-sm font-semibold"
                            />
                            <textarea
                              value={editingNote.content}
                              onChange={(e) => setEditingNote({...editingNote, content: e.target.value})}
                              rows="4"
                              className="w-full px-3 py-2 border-2 border-slate-200 rounded-lg text-sm resize-none"
                            />
                            <div className="flex items-center justify-between">
                              <select
                                value={editingNote.category}
                                onChange={(e) => setEditingNote({...editingNote, category: e.target.value})}
                                className="px-3 py-1 border-2 border-slate-200 rounded-lg text-xs"
                              >
                                <option value="general">General</option>
                                <option value="important">Important</option>
                                <option value="reminder">Reminder</option>
                                <option value="todo">To-Do</option>
                                <option value="meeting">Meeting Notes</option>
                                <option value="idea">Ideas</option>
                              </select>
                              <div className="flex gap-2">
                                <button
                                  onClick={() => setEditingNote(null)}
                                  className="px-3 py-1 text-slate-600 border border-slate-300 rounded-lg hover:bg-slate-50 text-sm"
                                >
                                  Cancel
                                </button>
                                <button
                                  onClick={() => handleUpdateNote(note.id)}
                                  className="px-3 py-1 bg-green-500 text-white rounded-lg hover:bg-green-600 text-sm"
                                >
                                  Save
                                </button>
                              </div>
                            </div>
                          </div>
                        ) : (
                          // View Mode
                          <>
                            <div className="flex items-start justify-between mb-2">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-1">
                                  <h5 className="font-semibold text-slate-900">{note.title}</h5>
                                  {note.is_important && (
                                    <Star className="w-4 h-4 text-red-500 fill-current" />
                                  )}
                                </div>
                                <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium border ${getCategoryColor(note.category)}`}>
                                  {note.category}
                                </span>
                              </div>
                              <div className="flex items-center gap-1">
                                <button
                                  onClick={() => setEditingNote(note)}
                                  className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                  title="Edit note"
                                >
                                  <Edit2 className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() => handleDeleteNote(note.id)}
                                  className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                  title="Delete note"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() => setExpandedNote(expandedNote === note.id ? null : note.id)}
                                  className="p-1.5 text-slate-600 hover:bg-slate-50 rounded-lg transition-colors"
                                >
                                  {expandedNote === note.id ? (
                                    <ChevronUp className="w-4 h-4" />
                                  ) : (
                                    <ChevronDown className="w-4 h-4" />
                                  )}
                                </button>
                              </div>
                            </div>
                            
                            <p className={`text-sm text-slate-600 whitespace-pre-wrap ${
                              expandedNote === note.id ? '' : 'line-clamp-2'
                            }`}>
                              {note.content}
                            </p>
                            
                            <div className="flex items-center justify-between mt-3 pt-3 border-t border-slate-200">
                              <div className="flex items-center text-xs text-slate-500">
                                <Calendar className="w-3.5 h-3.5 mr-1" />
                                {new Date(note.created_at).toLocaleDateString('en-US', {
                                  month: 'short',
                                  day: 'numeric',
                                  year: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })}
                              </div>
                              {note.updated_at !== note.created_at && (
                                <span className="text-xs text-slate-400">
                                  Updated {new Date(note.updated_at).toLocaleDateString('en-US', {
                                    month: 'short',
                                    day: 'numeric'
                                  })}
                                </span>
                              )}
                            </div>
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Footer - Only show for Add mode */}
          {viewMode === 'add' && (
            <div className="bg-slate-50 p-6 rounded-b-2xl flex items-center justify-end space-x-3 border-t border-slate-200">
              <button
                onClick={() => {
                  setNoteTitle('');
                  setNoteContent('');
                  setNoteCategory('general');
                  setNoteIsImportant(false);
                  setShowNotesPanel(false);
                  setViewMode('add');
                }}
                className="px-5 py-2.5 text-slate-600 border-2 border-slate-300 rounded-xl hover:bg-white transition-all text-sm font-medium"
                disabled={noteSubmitting}
              >
                Cancel
              </button>
              <button
                onClick={handleAddNote}
                disabled={noteSubmitting || !noteTitle.trim() || !noteContent.trim()}
                className="px-6 py-2.5 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-xl hover:from-green-600 hover:to-emerald-700 transition-all text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2 shadow-lg hover:shadow-xl"
              >
                {noteSubmitting ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    <span>Saving...</span>
                  </>
                ) : (
                  <>
                    <FileText className="w-4 h-4" />
                    <span>Save Note</span>
                  </>
                )}
              </button>
            </div>
          )}
        </div>
      </div>

      <style jsx>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        
        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(20px) scale(0.95);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }
        
        .animate-fadeIn {
          animation: fadeIn 0.2s ease-out;
        }
        
        .animate-slideUp {
          animation: slideUp 0.3s ease-out;
        }

        .line-clamp-2 {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
      `}</style>
    </>
  );
};

export default EnhancedPersonalNotesModal;